/**
 * Cloud Licence API Helper
 *
 * Standalone API module for the Cloud Licence system.
 * Uses its own JWT auth (separate from FlexFlow ERP auth).
 * Token stored in sessionStorage under "clAdminToken".
 */

const CL_BASE = "/api"
const TOKEN_KEY = "clAdminToken"
const TOKEN_EXPIRY_KEY = "clAdminTokenExpiry"

// ─── Token Management ────────────────────────────────────────────────────────

export function getCLToken() {
  const token = sessionStorage.getItem(TOKEN_KEY)
  const expiry = parseInt(sessionStorage.getItem(TOKEN_EXPIRY_KEY) || "0")
  if (!token || Date.now() > expiry) {
    clearCLToken()
    return null
  }
  return token
}

export function setCLToken(token, expiresIn = "30m") {
  // Parse expiresIn string to ms (e.g. "30m" → 30*60*1000)
  let ms = 29 * 60 * 1000 // default 29 min
  if (typeof expiresIn === "string") {
    const match = expiresIn.match(/^(\d+)([mhd])$/)
    if (match) {
      const val = parseInt(match[1])
      const unit = match[2]
      if (unit === "m") ms = val * 60 * 1000
      else if (unit === "h") ms = val * 60 * 60 * 1000
      else if (unit === "d") ms = val * 24 * 60 * 60 * 1000
    }
  }
  sessionStorage.setItem(TOKEN_KEY, token)
  sessionStorage.setItem(TOKEN_EXPIRY_KEY, (Date.now() + ms).toString())
}

export function clearCLToken() {
  sessionStorage.removeItem(TOKEN_KEY)
  sessionStorage.removeItem(TOKEN_EXPIRY_KEY)
}

export function getCLTokenExpiry() {
  return parseInt(sessionStorage.getItem(TOKEN_EXPIRY_KEY) || "0")
}

// ─── API Calls ───────────────────────────────────────────────────────────────

export async function clLogin(username, password) {
  const res = await fetch(`${CL_BASE}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || "Login failed")
  setCLToken(data.token, data.expiresIn)
  return data
}

export async function clGet(path) {
  const token = getCLToken()
  if (!token) throw new Error("Not authenticated")
  const res = await fetch(`${CL_BASE}${path}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  })
  const data = await res.json()
  if (res.status === 401) {
    clearCLToken()
    throw new Error("Session expired")
  }
  if (!res.ok) throw new Error(data.error || "Request failed")
  return data
}

export async function clPost(path, body) {
  const token = getCLToken()
  if (!token) throw new Error("Not authenticated")
  const res = await fetch(`${CL_BASE}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  })
  const data = await res.json()
  if (res.status === 401) {
    clearCLToken()
    throw new Error("Session expired")
  }
  if (!res.ok) throw new Error(data.error || "Request failed")
  return data
}

export async function clGetPrices() {
  const res = await fetch(`${CL_BASE}/checkout/prices`)
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || "Failed to load prices")
  return data
}
